import React, { useEffect, useMemo, useReducer, useState } from "react";

import openSocket from "../../services/socket-io";

import {
  Button,
  Checkbox,
  IconButton,
  makeStyles,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Divider,
  CircularProgress,
} from "@material-ui/core";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { Add, DeleteOutline, Edit } from "@material-ui/icons";
import QueueModal from "../../components/QueueModal";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";
import {
  buildWhatsappTabs,
  getWhatsappTabParams,
  orphanTabPrefix,
} from "../../utils/connectionsTabs";

const recordOwnsWhatsapp = (record, connectionId) => {
  if (!connectionId) return true;
  const selectedValue = String(connectionId);

  if (selectedValue.startsWith(orphanTabPrefix)) {
    return String(record?.orphanedConnectionId || "") ===
      selectedValue.replace(orphanTabPrefix, "");
  }

  const ids = [
    record?.connectionId || record?.connection?.id,
    ...(record?.sharedConnectionIds || [])
  ]
    .map(Number)
    .filter(id => !Number.isNaN(id));
  return ids.includes(Number(selectedValue));
};

const useStyles = makeStyles((theme) => ({
  headerContent: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    [theme.breakpoints.down("xs")]: {
      alignItems: "stretch",
      flexDirection: "column",
      gap: theme.spacing(1),
    },
  },
  headerButtons: {
    [theme.breakpoints.down("xs")]: {
      display: "grid",
      gridTemplateColumns: "1fr",
      gap: theme.spacing(1),
      marginLeft: 0,
      "& > *": {
        margin: 0,
      },
      "& .MuiButton-root": {
        minHeight: 42,
      },
    },
  },
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "auto",
    margin: 20,
    ...theme.scrollbarStyles,
    [theme.breakpoints.down("xs")]: {
      margin: theme.spacing(1),
      padding: theme.spacing(1),
      border: 0,
      boxShadow: "none",
      backgroundColor: "transparent",
    },
  },
  customTableCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  colorPreview: {
    width: 60,
    height: 20,
    alignSelf: "center",
    borderRadius: 12,
    border: "1px solid rgba(0, 0, 0, 0.14)",
  },
  bulkActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0.5),
    flexWrap: "wrap",
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1, 0),
      alignItems: "stretch",
      "& .MuiButton-root": {
        flex: "1 1 auto",
      },
    },
  },
  bulkActionsText: {
    fontWeight: 600,
    marginRight: "auto",
  },
  tableWrapper: {
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  mobileCards: {
    display: "none",
    [theme.breakpoints.down("xs")]: {
      display: "grid",
      gap: theme.spacing(1.5),
    },
  },
  mobileCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(1.5),
  },
  mobileCardHeader: {
    display: "grid",
    gridTemplateColumns: "auto 1fr auto",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  mobileTitle: {
    fontWeight: 600,
    lineHeight: 1.25,
    wordBreak: "break-word",
  },
  mobileMeta: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    lineHeight: 1.35,
    wordBreak: "break-word",
  },
  mobileCardGrid: {
    display: "grid",
    gap: theme.spacing(1.25),
    marginTop: theme.spacing(1.25),
  },
  mobileField: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    minHeight: 32,
  },
  mobileLabel: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  mobileValue: {
    minWidth: 0,
    textAlign: "right",
    wordBreak: "break-word",
  },
  mobileActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(0.5),
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_QUEUES") {
    const queues = action.payload;
    const newQueues = [];

    queues.forEach((queue) => {
      const queueIndex = state.findIndex((q) => q.id === queue.id);
      if (queueIndex !== -1) {
        state[queueIndex] = queue;
      } else {
        newQueues.push(queue);
      }
    });

    return [...state, ...newQueues];
  }

  if (action.type === "UPDATE_QUEUES") {
    const queue = action.payload;
    const queueIndex = state.findIndex((u) => u.id === queue.id);

    if (queueIndex !== -1) {
      state[queueIndex] = queue;
      return [...state];
    } else {
      return [queue, ...state];
    }
  }

  if (action.type === "DELETE_QUEUE") {
    const queueId = action.payload;
    const queueIndex = state.findIndex((q) => q.id === queueId);
    if (queueIndex !== -1) {
      state.splice(queueIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const Queues = () => {
  const classes = useStyles();

  const [queues, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);

  const [queueModalOpen, setQueueModalOpen] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [connections, setConnections] = useState([]);
  const [orphanGroups, setOrphanGroups] = useState([]);
  const [selectedConnectionId, setSelectedConnectionId] = useState("");
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const whatsappTabs = useMemo(
    () => buildWhatsappTabs(connections, orphanGroups),
    [connections, orphanGroups]
  );
  const selectedWhatsappTab = whatsappTabs.find(
    tab => String(tab.value) === String(selectedConnectionId)
  );

  useEffect(() => {
    (async () => {
      try {
        const [{ data }, { data: orphanData }] = await Promise.all([
          api.get("/connections/"),
          api.get("/connections/orphan-groups")
        ]);
        const loadedConnections = data || [];
        const loadedOrphans = orphanData || [];
        const tabs = buildWhatsappTabs(loadedConnections, loadedOrphans);
        setConnections(loadedConnections);
        setOrphanGroups(loadedOrphans);
        setSelectedConnectionId(prevState => prevState || tabs[0]?.value || "");
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedConnectionId) return;

    (async () => {
      setLoading(true);
      dispatch({ type: "RESET" });
      setSelectedQueueIds([]);
      try {
        const { data } = await api.get("/queue", {
          params: getWhatsappTabParams(selectedConnectionId),
        });
        dispatch({ type: "LOAD_QUEUES", payload: data });
      } catch (err) {
        toastError(err);
      }
      setLoading(false);
    })();
  }, [selectedConnectionId]);

  useEffect(() => {
    const queueIds = new Set(queues.map(queue => String(queue.id)));
    setSelectedQueueIds(prevState =>
      prevState.filter(queueId => queueIds.has(String(queueId)))
    );
  }, [queues]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("queue", (data) => {
      if (data.action === "update" || data.action === "create") {
        if (
          !selectedConnectionId ||
          recordOwnsWhatsapp(data.queue, selectedConnectionId)
        ) {
          dispatch({ type: "UPDATE_QUEUES", payload: data.queue });
        } else if (data.action === "update") {
          dispatch({ type: "DELETE_QUEUE", payload: data.queue.id });
        }
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_QUEUE", payload: data.queueId });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [selectedConnectionId]);

  const handleOpenQueueModal = () => {
    setQueueModalOpen(true);
    setSelectedQueue(null);
  };

  const handleCloseQueueModal = () => {
    setQueueModalOpen(false);
    setSelectedQueue(null);
  };

  const handleEditQueue = (queue) => {
    setSelectedQueue(queue);
    setQueueModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedQueue(null);
  };

  const handleDeleteQueue = async (queueId) => {
    try {
      await api.delete(`/queue/${queueId}`);
      toast.success(i18n.t("queues.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setSelectedQueue(null);
  };

  const allQueuesSelected =
    queues.length > 0 &&
    queues.every(queue => selectedQueueIds.includes(String(queue.id)));
  const selectedQueueCount = selectedQueueIds.length;

  const toggleQueueSelection = queueId => {
    const normalizedId = String(queueId);
    setSelectedQueueIds(prevState =>
      prevState.includes(normalizedId)
        ? prevState.filter(id => id !== normalizedId)
        : [...prevState, normalizedId]
    );
  };

  const toggleAllQueuesSelection = () => {
    if (allQueuesSelected) {
      setSelectedQueueIds([]);
      return;
    }

    setSelectedQueueIds(queues.map(queue => String(queue.id)));
  };

  const handleDeleteSelectedQueues = async () => {
    try {
      await Promise.all(
        selectedQueueIds.map(queueId => api.delete(`/queue/${queueId}`))
      );
      selectedQueueIds.forEach(queueId =>
        dispatch({ type: "DELETE_QUEUE", payload: Number(queueId) })
      );
      setSelectedQueueIds([]);
      toast.success(i18n.t("queues.toasts.deleted"));
    } catch (err) {
      toastError(err);
    } finally {
      setBulkDeleteOpen(false);
    }
  };

  const renderQueueActions = queue => (
    <>
      <IconButton size="small" onClick={() => handleEditQueue(queue)}>
        <Edit />
      </IconButton>

      <IconButton
        size="small"
        onClick={() => {
          setSelectedQueue(queue);
          setConfirmModalOpen(true);
        }}
      >
        <DeleteOutline />
      </IconButton>
    </>
  );

  const renderMobileQueueCard = queue => (
    <div className={classes.mobileCard} key={queue.id}>
      <div className={classes.mobileCardHeader}>
        <Checkbox
          size="small"
          color="primary"
          checked={selectedQueueIds.includes(String(queue.id))}
          onChange={() => toggleQueueSelection(queue.id)}
          inputProps={{ "aria-label": `Seleccionar ${queue.name}` }}
        />
        <div>
          <Typography className={classes.mobileTitle}>{queue.name}</Typography>
          <Typography className={classes.mobileMeta}>
            {queue.connection?.name || queue.connection?.number || "-"}
          </Typography>
        </div>
        <div className={classes.mobileActions}>{renderQueueActions(queue)}</div>
      </div>

      <Divider />

      <div className={classes.mobileCardGrid}>
        <div className={classes.mobileField}>
          <span className={classes.mobileLabel}>Conexión</span>
          <Typography variant="body2" className={classes.mobileValue}>
            {queue.connection?.name || queue.connection?.number || "-"}
          </Typography>
        </div>
        <div className={classes.mobileField}>
          <span className={classes.mobileLabel}>{i18n.t("queues.table.color")}</span>
          <span className={classes.colorPreview} style={{ backgroundColor: queue.color }} />
        </div>
        <div className={classes.mobileField}>
          <span className={classes.mobileLabel}>
            {i18n.t("queues.table.greeting")}
          </span>
          <Typography variant="body2" className={classes.mobileValue}>
            {queue.greetingMessage || "-"}
          </Typography>
        </div>
      </div>
    </div>
  );

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          selectedQueue &&
          `${i18n.t("queues.confirmationModal.deleteTitle")} ${
            selectedQueue.name
          }?`
        }
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={() => handleDeleteQueue(selectedQueue.id)}
      >
        {i18n.t("queues.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <ConfirmationModal
        title={`Eliminar ${selectedQueueCount} área${
          selectedQueueCount === 1 ? "" : "s"
        }?`}
        open={bulkDeleteOpen}
        onClose={setBulkDeleteOpen}
        onConfirm={handleDeleteSelectedQueues}
      >
        {i18n.t("queues.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <QueueModal
        open={queueModalOpen}
        onClose={handleCloseQueueModal}
        queueId={selectedQueue?.id}
        defaultConnectionId={selectedConnectionId}
      />
      <MainHeader>
        <div className={classes.headerContent}>
          <Title>{i18n.t("queues.title")}</Title>
          <MainHeaderButtonsWrapper className={classes.headerButtons}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              disabled={!selectedConnectionId || selectedWhatsappTab?.orphan}
              onClick={handleOpenQueueModal}
            >
              {i18n.t("queues.buttons.add")}
            </Button>
          </MainHeaderButtonsWrapper>
        </div>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Tabs
          value={selectedConnectionId}
          onChange={(event, value) => setSelectedConnectionId(value)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          {whatsappTabs.length ? (
            whatsappTabs.map(whatsapp => (
              <Tab
                key={whatsapp.value}
                value={whatsapp.value}
                label={whatsapp.name || whatsapp.number || `Conexión ${whatsapp.id}`}
              />
            ))
          ) : (
            <Tab value="" label="Sin conexiones" disabled />
          )}
        </Tabs>
        {queues.length > 0 && (
          <div className={classes.bulkActions}>
            <Checkbox
              size="small"
              color="primary"
              checked={allQueuesSelected}
              indeterminate={selectedQueueCount > 0 && !allQueuesSelected}
              onChange={toggleAllQueuesSelection}
              inputProps={{ "aria-label": "Seleccionar áreas" }}
            />
            <Typography variant="body2" className={classes.bulkActionsText}>
              {selectedQueueCount
                ? `${selectedQueueCount} seleccionado${
                    selectedQueueCount === 1 ? "" : "s"
                  }`
                : "Seleccionar áreas"}
            </Typography>
            {selectedQueueCount > 0 && (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setSelectedQueueIds([])}
                >
                  Limpiar
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="secondary"
                  startIcon={<DeleteOutline />}
                  onClick={() => setBulkDeleteOpen(true)}
                >
                  Eliminar
                </Button>
              </>
            )}
          </div>
        )}
        <div className={classes.mobileCards}>
          {queues.map(renderMobileQueueCard)}
          {loading && <CircularProgress size={24} />}
        </div>

        <Table size="small" className={classes.tableWrapper}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell align="center">
                {i18n.t("queues.table.name")}
              </TableCell>
              <TableCell align="center">
                Conexión
              </TableCell>
              <TableCell align="center">
                {i18n.t("queues.table.color")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("queues.table.greeting")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("queues.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {queues.map((queue) => (
                <TableRow key={queue.id}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      color="primary"
                      checked={selectedQueueIds.includes(String(queue.id))}
                      onChange={() => toggleQueueSelection(queue.id)}
                      inputProps={{ "aria-label": `Seleccionar ${queue.name}` }}
                    />
                  </TableCell>
                  <TableCell align="center">{queue.name}</TableCell>
                  <TableCell align="center">
                    {queue.connection?.name || queue.connection?.number || "-"}
                  </TableCell>
                  <TableCell align="center">
                    <div className={classes.customTableCell}>
                      <span
                        className={classes.colorPreview}
                        style={{
                          backgroundColor: queue.color,
                        }}
                      />
                    </div>
                  </TableCell>
                  <TableCell align="center">
                    <div className={classes.customTableCell}>
                      <Typography
                        style={{ width: 300, align: "center" }}
                        noWrap
                        variant="body2"
                      >
                        {queue.greetingMessage}
                      </Typography>
                    </div>
                  </TableCell>
                  <TableCell align="center">
                    {renderQueueActions(queue)}
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={6} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Queues;

